<template>
<layout-content>
  <template #toolbar>
    <data-time-query :query="query" />

    <el-select
      v-model="query.siteId"
      size="small"
      style="width:120px;margin-left:.5rem;"
      @change="onSiteIdChanged"
    >
      <el-option
        v-for="site in filterSites"
        :key="site.id"
        :value="site.id"
        :label="site.name"
      />
    </el-select>

    <el-select
      v-model="query.deviceCategoryId"
      size="small"
      style="width:120px;margin-left:.5rem;"
      @change="onDeviceCategoryIdChanged"
    >
      <el-option
        v-for="deviceCategory in queryDeviceCategories"
        :key="deviceCategory.value"
        :value="deviceCategory.value"
        :label="deviceCategory.label"
      />
    </el-select>

    <el-select
      v-model="query.checkItemId"
      size="small"
      style="width:120px;margin-left:.5rem;"
      @change="onSearchBtnClick"
    >
      <el-option
        v-for="checkItem in queryCheckItems"
        :key="checkItem.value"
        :value="checkItem.value"
        :label="checkItem.label"
      />
    </el-select>

    <el-button
      type="orange"
      size="small"
      icon="el-icon-search"
      style="margin-left:1rem;"
      @click="onSearchBtnClick"
    >查询</el-button>

    <el-button
      icon="el-icon-upload"
      type="primary"
      size="small"
      @click="onImportBtnClick"
    >导入</el-button>

    <el-button
      icon="el-icon-delete"
      type="danger"
      size="small"
      @click="onDeleteBtnClick"
    >删除</el-button>
  </template>

  <template #body>
    <data-wrapper>
      <q-c-chart
        v-if="currentCheckItem"
        :data="qcData"
        :qc-device="currentCheckItem.deviceCategory"
        :qc-type="currentCheckItem.checkItem"
        :date-range="[currentQuery.beginTime, currentQuery.endTime]"
        :site="currentCheckItem.site"
        :show-unmatched-btn="unmatchedFactors.length > 0"
        @unmatched-btn-click="onUnmatchedBtnClick"
      />
      <div v-else style="padding-top:50px;text-align:center;">该站点没有配置过质控因子分类</div>
    </data-wrapper>
  </template>

  <template #editor>
    <q-c-data-import
      v-if="currentCheckItem"
      ref="qcDataImport"
      :site="currentCheckItem.site"
      :qc-device-list="currentCheckItem.deviceCategories"
      :query="query"
      @imported-success="onSearchBtnClick"
    />
    <q-c-data-delete
      v-if="currentCheckItem"
      ref="qcDataDelete"
      :site="currentCheckItem.site"
      :qc-device-list="currentCheckItem.deviceCategories"
      :query="query"
      @deleted-success="onSearchBtnClick"
    />

    <match-factor-dialog
      ref="match-factor-dialog"
      :unmatched-factors="unmatchedFactors"
      :unmatched-std-factors="unmatchedStdFactors"
      @saved-success="onSearchBtnClick"
    />
  </template>
</layout-content>
</template>

<script>
import BaseSiteDeviceData from '../../BaseSiteDeviceData'
import DeviceCategoryConfig from '../../../model/DeviceCategoryConfig'
import qcDeviceConfig from '../../../utils/qcDeviceConfig'

import {mapGetters} from 'vuex'

import LayoutContent from '../../../components/Layout/Content'
import DataTimeQuery from '../../../components/Data/Query/DataTimeQuery'
import DataWrapper from '../../../components/Data/DataWrapper'
import QCChart from './QCChart'
import QCDataImport from './QCDataImport'
import QCDataDelete from './QCDataDelete'
import MatchFactorDialog from './MatchFactorDialog'

export default {
  name: 'QCAnalysisData',
  routeInfo: {
    label: '质控统计'
  },
  extends: BaseSiteDeviceData,
  components: {MatchFactorDialog, LayoutContent, DataTimeQuery, DataWrapper, QCChart, QCDataImport, QCDataDelete},
  data: () => ({
    query: {
      siteId: null,
      deviceCategoryId: null,
      checkItemId: null
    },
    qcData: Object.freeze({}),
    unmatchedStdFactors: Object.freeze([])
  }),
  computed: {
    ...mapGetters({
      factorMap: 'Factor/map'
    }),
    queryTimeStep: () => 30,
    endTimeDiff: () => 0,
    siteDeviceCategoryConfig: () => [
      new DeviceCategoryConfig(1, true),
      new DeviceCategoryConfig(process.env.VOCS_DEVICE_CATEGORY, false),
      new DeviceCategoryConfig(process.env.VOCS_CALIBRATOR, false),
      new DeviceCategoryConfig(process.env.NMHC_DEVICE_CATEGORY, false),
      new DeviceCategoryConfig(process.env.PAN_DEVICE_CATEGORY, false),
      new DeviceCategoryConfig(process.env.OCEC_DEVICE_CATEGORY, false),
      new DeviceCategoryConfig(process.env.ION_DEVICE_CATEGORY, false),
      new DeviceCategoryConfig(process.env.HEAVY_METAL_DEVICE_CATEGORY, false),
      new DeviceCategoryConfig(process.env.NORMAL_CALIBRATOR, false),
      new DeviceCategoryConfig(process.env.ORGANIC_SULFUR_DEVICE_CATEGORY, false),
      new DeviceCategoryConfig(process.env.NO2_DIRECT_DETECTION_DEVICE_CATEGORY, false),
      new DeviceCategoryConfig(process.env.NOY_DEVICE_CATEGORY, false),
      new DeviceCategoryConfig(process.env.HCHO_DEVICE_CATEGORY, false)
    ],
    queryDeviceCategories () {
      let {siteId} = this.query
      let qcConfigMap = qcDeviceConfig.map

      return siteId ? Object.values(this.filterSiteMap[siteId].devices.reduce((map, device) => {
        let config = qcConfigMap[device.factorCategory || device.categoryId]

        if (config) {
          let {value} = config

          if (!map[value]) {
            config.device = device
            map[value] = config
          }
        }

        return map
      }, {})).sort((a, b) => a.sort - b.sort) : []
      // .sort((a, b) => a.categoryId - b.categoryId) : []
    },
    queryCheckItems () {
      let {deviceCategoryId} = this.query
      return deviceCategoryId ? this.queryDeviceCategories.find(deviceCategory => deviceCategory.value === deviceCategoryId).children : []
    },
    currentCheckItem () {
      let {currentQuery} = this
      let {siteId, deviceCategoryId, checkItemId} = currentQuery

      if (!siteId || !deviceCategoryId || !checkItemId) {
        return null
      }

      let site = this.filterSiteMap[siteId]
      let qcConfigMap = qcDeviceConfig.map
      let deviceCategories = site.devices.filter(device => qcConfigMap[device.factorCategory || device.categoryId]).sort((a, b) => a.categoryId - b.categoryId).map(device => qcConfigMap[device.factorCategory || device.categoryId])
      let deviceCategory = deviceCategories.find(deviceCategory => deviceCategory.value === deviceCategoryId)

      return {
        site,
        deviceCategory,
        checkItem: deviceCategory.children.find(checkItem => checkItem.value === checkItemId),
        deviceCategories
      }
    },
    unmatchedFactors () {
      let {formatFactorMap} = this.qcData
      return formatFactorMap ? Object.values(formatFactorMap).filter(factor => !factor.factorId) : []
    }
  },
  methods: {
    setSiteIds () {
      let {filterSites} = this

      if (filterSites.length > 0) {
        let {query} = this

        if (!query.siteId) {
          query.siteId = filterSites[0].id

          let {queryDeviceCategories} = this
          query.deviceCategoryId = queryDeviceCategories[0].value

          let {queryCheckItems} = this
          query.checkItemId = queryCheckItems[0].value
        }

        this.afterSetSiteIds()
      }
    },
    setDataList (dataList) {
      let {currentCheckItem: {deviceCategory, checkItem}, factorMap} = this
      let tmp = dataList.reduce((map, data) => {
        let dataTime = data.dataTime
        let dateData = map[dataTime]

        if (!dateData) {
          dateData = map[dataTime] = {
            dataTime,
            data: {}
          }
        }

        let {factorId} = data
        let factor = factorMap[factorId]

        if (factor) {
          data.stdFactorName = factor.name
          data.parentId = factor.parentId
        } else {
          data.stdFactorName = factorId = data.factorName
          data.parentId = 'UNKNOWN'
        }

        dateData.data[factorId] = data

        return map
      }, {})

      let {convert} = checkItem
      let dataTimeDataList = Object.values(tmp)
      let formatFactorMap = dataTimeDataList.reduce((map, data) => {
        let factorData = data.data

        Object.entries(factorData).forEach(([factorId, factorData]) => {
          map[factorId] = {
            factorId: factorData.factorId,
            name: factorData.stdFactorName,
            parentId: factorData.parentId
          }
        })

        return map
      }, {})

      let factorIds = Object.keys(formatFactorMap).sort((a, b) => formatFactorMap[a].parentId.localeCompare(formatFactorMap[b].parentId))

      this.qcData = Object.freeze({
        deviceId: deviceCategory.value,
        deviceName: deviceCategory.label,
        statistics: deviceCategory.statistics,
        factorIds,
        formatFactorMap,
        data: dataTimeDataList.map(({dataTime, data}) => ({
          dataTime,
          data: factorIds.reduce((map, factorId) => {
            let factorData = data[factorId]

            if (factorData) {
              map[factorId] = factorData.value === -99999 ? null : convert(factorData.value)
            }

            return map
          }, {})
        })),
        ts: new Date().getTime()
      })

      let {device} = this.currentCheckItem.deviceCategory
      this.unmatchedStdFactors = Object.freeze([...device.siteDeviceFactors, ...device.siteDeviceFactors0].filter(siteDeviceFactor => !formatFactorMap[siteDeviceFactor.factorId]))
    },
    getData () {
      this.changeLoadStatus(true)

      this.$api.QCAnalysis.getList(this.currentQuery).then(this.setDataList).catch(console.error).finally(() => {
        this.changeLoadStatus(false)
      })
    },
    setCheckItemId () {
      let {query, queryCheckItems} = this
      let {checkItemId} = query

      if (queryCheckItems.some(checkItem => checkItem.value === checkItemId)) {
        return
      }

      query.checkItemId = queryCheckItems[0].value
    },
    setDeviceCategoryId () {
      let {query, queryDeviceCategories} = this
      let {deviceCategoryId} = query

      if (queryDeviceCategories.some(deviceCategory => deviceCategory.value === deviceCategoryId)) {
        return
      }

      query.deviceCategoryId = queryDeviceCategories[0].value
      this.setCheckItemId()
    },
    onSiteIdChanged () {
      this.setDeviceCategoryId()
      this.onSearchBtnClick()
    },
    onDeviceCategoryIdChanged () {
      this.setCheckItemId()
      this.onSearchBtnClick()
    },
    onImportBtnClick () {
      this.$refs.qcDataImport.open()
    },
    onDeleteBtnClick () {
      this.$refs.qcDataDelete.open()
    },
    onUnmatchedBtnClick () {
      this.$refs['match-factor-dialog'].open()
    }
  }
}
</script>

<style lang="scss" scoped>
.el-button {
  margin: 0 !important;
}
</style>



// WEBPACK FOOTER //
// src/views/05QC-Analysis/00Data/index.vue